<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <p class="page-subtitle mb-1">Verwaltung</p>
        <h1 class="page-title">Benutzerkonto bearbeiten</h1>
      </div>
      <button class="secondary-btn" @click="$router.push('/employees')">
        <v-icon size="16">mdi-arrow-left</v-icon>
        <span>Zurück</span>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="skel-card skel-big"></div>
    </div>

    <div v-else class="grid-split">
      <!-- Form -->
      <div class="card-modern">
        <h2 class="section-title mb-5">Benutzerdaten</h2>

        <div class="form-row">
          <div class="fg">
            <label class="fl">Vorname *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'firstname' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-account-outline</v-icon>
              <input v-model="employee.firstname" class="ii" placeholder="z.B. Max" @focus="focusField = 'firstname'" @blur="focusField = null" />
            </div>
          </div>
          <div class="fg">
            <label class="fl">Nachname *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'lastname' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-account-outline</v-icon>
              <input v-model="employee.lastname" class="ii" placeholder="z.B. Mustermann" @focus="focusField = 'lastname'" @blur="focusField = null" />
            </div>
          </div>
        </div>

        <div class="fg mb-4">
          <label class="fl">E-Mail *</label>
          <div class="iw" :class="{ 'iw-f': focusField === 'email' }">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-email-outline</v-icon>
            <input v-model="employee.email" type="email" class="ii" placeholder="z.B. max@example.com" @focus="focusField = 'email'" @blur="focusField = null" />
          </div>
        </div>

        <div class="fg mb-5">
          <label class="fl">Position *</label>
          <div class="iw" :class="{ 'iw-f': focusField === 'position' }">
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-briefcase-outline</v-icon>
            <input v-model="employee.position" class="ii" placeholder="z.B. Entwickler, Designer, Manager" @focus="focusField = 'position'" @blur="focusField = null" />
          </div>
        </div>

        <!-- Berechtigungen -->
        <div class="divider-soft mb-5"></div>

        <div class="d-flex align-center justify-space-between mb-3">
          <label class="fl" style="margin: 0">Berechtigungen</label>
          <div class="combo-wrap" ref="presetDropdown">
            <button type="button" class="iw iw-trigger preset-trigger" @click="presetMenuOpen = !presetMenuOpen">
              <v-icon size="14" color="#888">mdi-flash-outline</v-icon>
              <span class="ii-val">{{ activePresetName || 'Preset wählen…' }}</span>
              <v-icon size="14" color="#888" :class="{ 'chev-open': presetMenuOpen }">mdi-chevron-down</v-icon>
            </button>
            <div v-if="presetMenuOpen" class="combo-menu">
              <button
                v-for="p in presets"
                :key="p.id"
                type="button"
                class="combo-option"
                @click="applyPreset(p)"
              >
                <v-icon size="13" :color="p.isSystem ? '#5B5BD6' : '#0a1628'">{{ p.isSystem ? 'mdi-shield-star-outline' : 'mdi-bookmark-outline' }}</v-icon>
                <span class="combo-label">{{ p.name }}</span>
                <span v-if="p.isSystem" class="preset-system-tag">System</span>
                <button
                  v-else
                  type="button"
                  class="preset-delete-btn"
                  @click.stop="confirmDeletePreset(p)"
                  title="Preset löschen"
                >
                  <v-icon size="12">mdi-delete-outline</v-icon>
                </button>
              </button>
              <div class="combo-divider"></div>
              <button type="button" class="combo-option combo-option-create" @click="openSavePresetDialog">
                <v-icon size="13" color="#5B5BD6">mdi-plus-circle-outline</v-icon>
                <span class="combo-label">Aktuelle Auswahl als Preset speichern…</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Reviewer-Rollen (separat behandelt, nicht Teil der allgemeinen Permissions) -->
        <div class="perm-module">
          <div class="perm-module-header">
            <v-icon size="14" color="#5B5BD6">mdi-clipboard-check-outline</v-icon>
            <span>Review-Rollen</span>
          </div>
          <div class="perm-row">
            <button
              class="perm-pill"
              :class="{ 'perm-pill-active': employee.isTechnicalReviewer === 1 }"
              @click="employee.isTechnicalReviewer = employee.isTechnicalReviewer === 1 ? 0 : 1"
            >
              <v-icon size="12">mdi-code-tags</v-icon>
              Technischer Reviewer
            </button>
            <button
              class="perm-pill"
              :class="{ 'perm-pill-active': employee.isVisualReviewer === 1 }"
              @click="employee.isVisualReviewer = employee.isVisualReviewer === 1 ? 0 : 1"
            >
              <v-icon size="12">mdi-eye-outline</v-icon>
              Optischer Reviewer
            </button>
          </div>
        </div>

        <!-- Permission-Matrix nach Modul -->
        <div v-for="m in modules" :key="m.key" class="perm-module">
          <div class="perm-module-header">
            <v-icon size="14" color="#0a1628">{{ m.icon }}</v-icon>
            <span>{{ m.label }}</span>
          </div>
          <div class="perm-row">
            <button
              v-for="a in m.actions"
              :key="`${m.key}.${a}`"
              class="perm-pill"
              :class="{ 'perm-pill-active': isPermActive(m.key, a) }"
              @click="togglePerm(m.key, a)"
            >
              <v-icon size="11">{{ permIcon(a) }}</v-icon>
              {{ actionLabels[a] || a }}
            </button>
          </div>
        </div>

        <div v-if="saveMessage" class="mt-4" :class="saveSuccess ? 'alert-success' : 'alert-error'">
          <v-icon size="16" :color="saveSuccess ? '#2E7D32' : '#D32F2F'">
            {{ saveSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
          </v-icon>
          {{ saveMessage }}
        </div>
      </div>

      <!-- Save-Preset Dialog -->
      <div v-if="savePresetDialog" class="dialog-backdrop" @click.self="savingPreset || (savePresetDialog = false)"></div>
      <v-dialog v-model="savePresetDialog" max-width="440" :scrim="false" persistent>
        <v-card rounded="xl" class="modern-dialog" elevation="0">
          <button class="close-btn" @click="savePresetDialog = false"><v-icon size="18">mdi-close</v-icon></button>
          <div class="dh">
            <div class="dh-icon"><v-icon size="22" color="#0a1628">mdi-flash-outline</v-icon></div>
            <div>
              <div class="dh-title">Preset speichern</div>
              <div class="dh-sub">Aktuelle Berechtigungs-Auswahl als wiederverwendbares Preset.</div>
            </div>
          </div>
          <div class="db">
            <label class="field-label-sm">Name</label>
            <input v-model="newPresetName" type="text" class="text-input-sm" placeholder="z.B. Junior Developer" />
            <div v-if="presetError" class="alert-error mt-3">
              <v-icon size="14" color="#D32F2F">mdi-alert-circle-outline</v-icon>
              {{ presetError }}
            </div>
          </div>
          <div class="dff">
            <button class="bo" @click="savePresetDialog = false" :disabled="savingPreset">Abbrechen</button>
            <button class="bp" @click="saveAsPreset" :disabled="savingPreset || !newPresetName.trim()">
              <v-progress-circular v-if="savingPreset" indeterminate size="14" width="2" color="white" />
              <v-icon v-else size="15">mdi-content-save</v-icon>
              Preset speichern
            </button>
          </div>
        </v-card>
      </v-dialog>

      <!-- Avatar -->
      <div class="card-modern avatar-side">
        <h2 class="section-title mb-4">
          <v-icon size="18" color="#0a1628" class="mr-2">mdi-account-circle-outline</v-icon>
          Avatar
        </h2>

        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style="display: none"
          @change="onFileSelected"
        />

        <div class="avatar-preview" @click="triggerFileDialog">
          <v-img
            :src="imagePreview || `/api/employees/${$route.params.id}/image`"
            cover
            aspect-ratio="1"
          >
            <template #error>
              <div class="avatar-placeholder">
                <v-icon size="44" color="#c5cbd3" class="mb-2">mdi-account-circle</v-icon>
                <div class="avatar-fallback-text">Kein Avatar</div>
              </div>
            </template>
          </v-img>
        </div>

        <button class="bs mt-3" @click="triggerFileDialog">
          <v-icon size="15">mdi-image-edit-outline</v-icon>
          Avatar ändern
        </button>

        <button
          v-if="newAvatarFile"
          class="submit-btn mt-3"
          :disabled="uploadingAvatar"
          @click="uploadAvatar"
        >
          <v-progress-circular v-if="uploadingAvatar" indeterminate size="14" width="2" color="white" />
          <v-icon v-else size="16">mdi-upload-outline</v-icon>
          <span>Avatar hochladen</span>
        </button>

        <div v-if="avatarMessage" class="mt-3" :class="avatarSuccess ? 'alert-success' : 'alert-error'">
          <v-icon size="16" :color="avatarSuccess ? '#2E7D32' : '#D32F2F'">
            {{ avatarSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
          </v-icon>
          {{ avatarMessage }}
        </div>
      </div>
    </div>

    <!-- Floating Save Button (mit kurzer Erfolgs-Animation) -->
    <transition name="slide-fade">
      <div v-if="hasChanges || justSaved" class="save-floating">
        <button
          class="save-btn"
          :class="{ 'save-btn-success': justSaved }"
          :disabled="saving || justSaved"
          @click="saveChanges"
        >
          <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
          <v-icon v-else-if="justSaved" size="16">mdi-check-circle</v-icon>
          <v-icon v-else size="16">mdi-content-save-outline</v-icon>
          <span>{{ justSaved ? 'Gespeichert' : (saving ? 'Speichern…' : 'Änderungen speichern') }}</span>
        </button>
      </div>
    </transition>

    <!-- Toast-Stack (rechts unten) -->
    <div class="toast-stack">
      <transition-group name="toast">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="toast"
          :class="{ 'toast-error': t.type === 'error' }"
          @click="dismissToast(t.id)"
        >
          <div class="toast-icon" :class="{ 'toast-icon-error': t.type === 'error' }">
            <v-icon size="16" color="white">{{ t.type === 'error' ? 'mdi-alert-circle-outline' : 'mdi-check' }}</v-icon>
          </div>
          <div class="toast-body">
            <div class="toast-title">{{ t.title }}</div>
            <div v-if="t.message" class="toast-message">{{ t.message }}</div>
          </div>
          <button class="toast-close" @click.stop="dismissToast(t.id)">
            <v-icon size="14">mdi-close</v-icon>
          </button>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'EmployeeEdit',
  data() {
    return {
      employee: {
        firstname: '', lastname: '', email: '', position: '',
        isTechnicalReviewer: 0, isVisualReviewer: 0,
        permissions: {},
      },
      originalEmployee: {},
      imagePreview: null,
      newAvatarFile: null,
      loading: false,
      saving: false,
      uploadingAvatar: false,
      saveMessage: '',
      saveSuccess: false,
      avatarMessage: '',
      avatarSuccess: false,
      focusField: null,

      // Permission-System
      modules: [],
      actionLabels: {},
      presets: [],
      presetMenuOpen: false,
      savePresetDialog: false,
      savingPreset: false,
      newPresetName: '',
      presetError: '',

      // Toast + Save-Erfolg
      toasts: [],
      toastSeq: 0,
      justSaved: false,
    };
  },

  computed: {
    hasChanges() {
      return (
        this.employee.firstname !== this.originalEmployee.firstname ||
        this.employee.lastname !== this.originalEmployee.lastname ||
        this.employee.email !== this.originalEmployee.email ||
        this.employee.position !== this.originalEmployee.position ||
        this.employee.isTechnicalReviewer !== this.originalEmployee.isTechnicalReviewer ||
        this.employee.isVisualReviewer !== this.originalEmployee.isVisualReviewer ||
        JSON.stringify(this.employee.permissions || {}) !==
          JSON.stringify(this.originalEmployee.permissions || {})
      );
    },
    activePresetName() {
      // Findet ein Preset, das exakt zur aktuellen Auswahl passt
      const current = this.normalizedPermissions(this.employee.permissions);
      for (const p of this.presets) {
        if (
          this.permsEqual(p.permissions, current) &&
          (p.isTechnicalReviewer ? 1 : 0) === (this.employee.isTechnicalReviewer ? 1 : 0) &&
          (p.isVisualReviewer ? 1 : 0) === (this.employee.isVisualReviewer ? 1 : 0)
        ) {
          return p.name;
        }
      }
      return null;
    },
  },

  watch: {
    employee: {
      deep: true,
      handler() {
        if (this.saveMessage) this.saveMessage = '';
      },
    },
  },

  mounted() {
    this.loadEmployee();
    this.loadPermissionSchema();
    this.loadPresets();
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    handleOutsideClick(e) {
      if (this.presetMenuOpen) {
        const el = this.$refs.presetDropdown;
        if (el && !el.contains(e.target)) this.presetMenuOpen = false;
      }
    },

    async loadPermissionSchema() {
      try {
        const res = await axios.get('/api/permissions/keys');
        this.modules = res.data.data.modules || [];
        this.actionLabels = res.data.data.actionLabels || {};
      } catch (e) {
        console.error('Permission-Schema konnte nicht geladen werden', e);
      }
    },

    async loadPresets() {
      try {
        const res = await axios.get('/api/permissions/presets');
        this.presets = res.data.data || [];
      } catch (e) {
        console.error('Presets konnten nicht geladen werden', e);
      }
    },

    isPermActive(moduleKey, action) {
      const p = this.employee.permissions || {};
      return p[`${moduleKey}.${action}`] === 1 || p[`${moduleKey}.${action}`] === true;
    },
    togglePerm(moduleKey, action) {
      const key = `${moduleKey}.${action}`;
      const current = this.employee.permissions || {};
      const next = { ...current };
      next[key] = this.isPermActive(moduleKey, action) ? 0 : 1;
      this.employee.permissions = next;
    },
    permIcon(action) {
      return ({
        view:    'mdi-eye-outline',
        create:  'mdi-plus-circle-outline',
        edit:    'mdi-pencil-outline',
        delete:  'mdi-delete-outline',
        respond: 'mdi-message-reply-text-outline',
      })[action] || 'mdi-checkbox-marked-circle-outline';
    },

    normalizedPermissions(p) {
      const out = {};
      const src = p || {};
      for (const k of Object.keys(src)) {
        out[k] = src[k] === 1 || src[k] === true ? 1 : 0;
      }
      return out;
    },
    permsEqual(a, b) {
      const A = this.normalizedPermissions(a);
      const B = this.normalizedPermissions(b);
      const keys = new Set([...Object.keys(A), ...Object.keys(B)]);
      for (const k of keys) {
        if ((A[k] || 0) !== (B[k] || 0)) return false;
      }
      return true;
    },

    applyPreset(p) {
      this.employee.permissions = { ...this.normalizedPermissions(p.permissions) };
      this.employee.isTechnicalReviewer = p.isTechnicalReviewer ? 1 : 0;
      this.employee.isVisualReviewer = p.isVisualReviewer ? 1 : 0;
      this.presetMenuOpen = false;
    },

    openSavePresetDialog() {
      this.newPresetName = '';
      this.presetError = '';
      this.savePresetDialog = true;
      this.presetMenuOpen = false;
    },
    async saveAsPreset() {
      const name = this.newPresetName.trim();
      if (!name) return;
      this.savingPreset = true;
      this.presetError = '';
      try {
        await axios.post('/api/permissions/presets/create', {
          name,
          permissions: this.normalizedPermissions(this.employee.permissions),
          isTechnicalReviewer: this.employee.isTechnicalReviewer,
          isVisualReviewer: this.employee.isVisualReviewer,
        });
        await this.loadPresets();
        this.savePresetDialog = false;
      } catch (e) {
        this.presetError = e.response?.data?.message || 'Fehler beim Speichern.';
      } finally {
        this.savingPreset = false;
      }
    },
    async confirmDeletePreset(p) {
      if (!confirm(`Preset "${p.name}" wirklich löschen?`)) return;
      try {
        await axios.post('/api/permissions/presets/delete', { id: p.id });
        await this.loadPresets();
      } catch (e) {
        alert(e.response?.data?.message || 'Fehler beim Löschen.');
      }
    },

    async loadEmployee() {
      this.loading = true;
      try {
        const result = await axios.get('/api/employees/get', { params: { id: this.$route.params.id } });
        const emp = result.data;
        // permissions kann String oder Object zurückkommen
        if (emp.permissions && typeof emp.permissions === 'string') {
          try { emp.permissions = JSON.parse(emp.permissions); } catch (_e) { emp.permissions = {}; }
        }
        emp.permissions = emp.permissions || {};
        this.employee = emp;
        this.originalEmployee = { ...emp, permissions: { ...emp.permissions } };
      } catch (e) {
        console.error('Fehler beim Laden!', e);
        this.saveMessage = 'Fehler beim Laden des Benutzers';
        this.saveSuccess = false;
      } finally {
        this.loading = false;
      }
    },

    async saveChanges() {
      this.saving = true;
      this.saveMessage = '';
      try {
        await axios.post('/api/employees/update', {
          id: this.$route.params.id,
          firstname: this.employee.firstname,
          lastname: this.employee.lastname,
          email: this.employee.email,
          position: this.employee.position,
          isTechnicalReviewer: this.employee.isTechnicalReviewer,
          isVisualReviewer: this.employee.isVisualReviewer,
          permissions: this.normalizedPermissions(this.employee.permissions),
        });
        this.saveMessage = '';
        this.saveSuccess = true;
        this.originalEmployee = { ...this.employee, permissions: { ...this.employee.permissions } };
        // Erfolgs-Animation am Save-Button
        this.justSaved = true;
        setTimeout(() => { this.justSaved = false; }, 1600);
        // Toast rechts unten
        this.showToast({
          type: 'success',
          title: 'Änderungen gespeichert',
          message: `${this.employee.firstname} ${this.employee.lastname}`,
        });
      } catch (error) {
        console.error('Fehler beim Speichern:', error);
        this.saveMessage = '';
        this.saveSuccess = false;
        this.showToast({
          type: 'error',
          title: 'Speichern fehlgeschlagen',
          message: error.response?.data?.message || 'Bitte erneut versuchen.',
        });
      } finally {
        this.saving = false;
      }
    },

    showToast(toast) {
      const id = ++this.toastSeq;
      this.toasts.push({ id, ...toast });
      const timeout = toast.type === 'error' ? 5000 : 3500;
      setTimeout(() => this.dismissToast(id), timeout);
    },
    dismissToast(id) {
      const idx = this.toasts.findIndex((t) => t.id === id);
      if (idx >= 0) this.toasts.splice(idx, 1);
    },

    triggerFileDialog() { this.$refs.fileInput.click(); },

    onFileSelected(e) {
      const file = e.target.files?.[0] || null;
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        this.avatarMessage = 'Die Datei ist größer als 5 MB.';
        this.avatarSuccess = false;
        return;
      }
      this.newAvatarFile = file;
      const reader = new FileReader();
      reader.onload = () => { this.imagePreview = reader.result; };
      reader.readAsDataURL(file);
    },

    async uploadAvatar() {
      if (!this.newAvatarFile) return;
      this.uploadingAvatar = true;
      this.avatarMessage = '';
      try {
        const fd = new FormData();
        fd.append('avatar', this.newAvatarFile);
        await axios.post(`/api/employees/${this.$route.params.id}/avatar`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.avatarMessage = 'Avatar erfolgreich aktualisiert!';
        this.avatarSuccess = true;
        this.newAvatarFile = null;
        setTimeout(() => { this.avatarMessage = ''; this.imagePreview = null; }, 3000);
      } catch (error) {
        console.error('Fehler beim Upload:', error);
        this.avatarMessage = error.response?.data?.message || 'Fehler beim Hochladen des Avatars!';
        this.avatarSuccess = false;
      } finally {
        this.uploadingAvatar = false;
      }
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 6px; }
.page-subtitle { font-size: 13px; color: #888; }

.secondary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.secondary-btn:hover { border-color: #0a1628; }

.grid-split { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 20px; align-items: start; }

.card-modern { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 28px; }
.section-title {
  font-size: 16px; font-weight: 700; color: #0a1628;
  display: flex; align-items: center;
}

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 16px; }

.fg { display: flex; flex-direction: column; }
.fg.mb-4 { margin-bottom: 16px; }
.fg.mb-5 { margin-bottom: 20px; }
.fl { font-size: 11px; font-weight: 600; color: #999; margin-bottom: 6px; letter-spacing: 0.3px; }

.iw {
  display: flex; align-items: center; gap: 10px;
  height: 42px; padding: 0 14px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; background: white; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit; height: 40px;
}
.ii::placeholder { color: #bbb; font-weight: 400; }

.divider-soft { height: 1px; background: #eceef2; }

/* Permission checkboxes */
.perm-option {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 16px; background: white; border: 1px solid #eceef2;
  border-radius: 12px; cursor: pointer; transition: all 0.15s;
}
.perm-option:hover { border-color: #c5cbd3; }
.perm-active { border-color: #0a1628; background: #fafbfc; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }

.perm-check {
  width: 22px; height: 22px; min-width: 22px; border-radius: 6px;
  border: 2px solid #c5cbd3; background: white;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.perm-check-on { background: #0a1628; border-color: #0a1628; }

.perm-icon {
  width: 36px; height: 36px; min-width: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
}
.perm-body { flex: 1; min-width: 0; }
.perm-title { font-size: 14px; font-weight: 700; color: #0a1628; margin-bottom: 2px; }
.perm-desc { font-size: 12px; color: #888; line-height: 1.5; }

/* Permission-Modul-Liste (neu) */
.perm-module {
  margin-bottom: 14px;
}
.perm-module:last-child { margin-bottom: 0; }
.perm-module-header {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 700; color: #0a1628;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eceef2;
  letter-spacing: -0.1px;
}
.perm-row {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.perm-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 11px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 999px;
  font-size: 11.5px; font-weight: 600; color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.perm-pill:hover { border-color: #c5cbd3; color: #0a1628; }
.perm-pill.perm-pill-active {
  background: #0a1628;
  border-color: #0a1628;
  color: white;
}

/* Preset-Trigger neben Berechtigungen-Label */
.preset-trigger {
  height: 34px !important;
  padding: 0 12px !important;
  font-size: 12px !important;
  min-width: 200px;
}
.preset-system-tag {
  font-size: 9px; font-weight: 700;
  color: #5B5BD6; background: rgba(91,91,214,0.08);
  padding: 2px 6px; border-radius: 4px;
  margin-left: auto;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.preset-delete-btn {
  margin-left: auto;
  width: 22px; height: 22px; padding: 0;
  background: transparent; border: none; border-radius: 5px;
  color: #c5cbd3; cursor: pointer;
}
.preset-delete-btn:hover { background: rgba(211,47,47,0.08); color: #D32F2F; }
.combo-option-create { color: #5B5BD6 !important; }
.combo-option-create .combo-label { color: #5B5BD6; font-weight: 600; }

/* Combo-Wrap + Trigger (für Preset-Picker) */
.combo-wrap { position: relative; }
.iw-trigger {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 0 12px;
  background: white !important;
  border: 1px solid #eceef2; border-radius: 8px;
  font-size: 13px; color: #0a1628;
  cursor: pointer;
  transition: border-color 0.15s;
  font-family: inherit;
  text-align: left;
}
.iw-trigger:hover { border-color: #c5cbd3; }
.ii-val {
  flex: 1; font-size: 12px; font-weight: 500; color: #0a1628;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.chev-open { transform: rotate(180deg); transition: transform 0.2s; }
.combo-menu {
  position: absolute; top: calc(100% + 6px); right: 0;
  min-width: 280px;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 360px; overflow-y: auto;
  padding: 6px;
  animation: comboFade 0.12s ease;
}
@keyframes comboFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.combo-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 8px 10px; background: transparent; border: none;
  border-radius: 7px; cursor: pointer; transition: background 0.1s;
  text-align: left; font-family: inherit; font-size: 13px;
}
.combo-option:hover { background: #fafbfc; }
.combo-label { flex: 1; color: #0a1628; font-weight: 500; }
.combo-divider { height: 1px; background: #eceef2; margin: 4px 0; }

/* Save-Preset Dialog */
.dialog-backdrop {
  position: fixed; inset: 0; background: rgba(10,22,40,0.5);
  z-index: 2000; animation: fadeBg 0.2s ease;
}
@keyframes fadeBg { from { opacity: 0; } to { opacity: 1; } }
.modern-dialog { background: white !important; position: relative; padding: 0; overflow: hidden; }
.close-btn {
  position: absolute; top: 16px; right: 16px;
  width: 32px; height: 32px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px;
  color: #888; cursor: pointer; z-index: 1;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; }
.dh { display: flex; align-items: center; gap: 14px; padding: 24px 28px 8px; }
.dh-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(10,22,40,0.06);
  display: flex; align-items: center; justify-content: center;
}
.dh-title { font-size: 17px; font-weight: 800; color: #0a1628; }
.dh-sub { font-size: 12px; color: #888; margin-top: 2px; }
.db { padding: 16px 28px; }
.dff {
  display: flex; gap: 10px; padding: 14px 28px 22px;
  border-top: 1px solid #f0f2f5;
}
.dff button { flex: 1; }
.bo {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 16px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.4; cursor: not-allowed; }
.bp {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 16px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; font-family: inherit;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }
.field-label-sm { display: block; font-size: 12px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.text-input-sm {
  width: 100%; padding: 10px 12px;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  font-size: 13px; color: #0a1628; font-family: inherit;
}
.text-input-sm:focus { outline: none; border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.alert-error {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 12px; background: rgba(211,47,47,0.05); color: #D32F2F;
  border: 1px solid rgba(211,47,47,0.2); border-radius: 8px;
  font-size: 12px; font-weight: 500;
}

/* Avatar side */
.avatar-side { position: sticky; top: 24px; }
.avatar-preview {
  border-radius: 12px; overflow: hidden;
  background: #f8f9fb; border: 1px solid #eceef2;
  padding: 8px; cursor: pointer; transition: all 0.15s;
}
.avatar-preview:hover { border-color: #0a1628; }
.avatar-preview :deep(.v-img) { border-radius: 8px; overflow: hidden; }
.avatar-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  aspect-ratio: 1; background: white; text-align: center;
}
.avatar-fallback-text { font-size: 11px; color: #888; }

.bs {
  display: inline-flex; align-items: center; gap: 8px; width: 100%; justify-content: center;
  padding: 10px 14px; border: 1px dashed #c5cbd3; border-radius: 10px; background: #fafbfc;
  font-size: 13px; font-weight: 600; color: #555; cursor: pointer; transition: all 0.15s;
  font-family: inherit;
}
.bs:hover { border-color: #0a1628; color: #0a1628; background: white; }

.submit-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 12px 18px;
  background: #2E7D32; color: white; border: none; border-radius: 10px;
  font-size: 13px; font-weight: 700; cursor: pointer;
  transition: background 0.15s; font-family: inherit;
}
.submit-btn:hover:not(:disabled) { background: #1B5E20; }
.submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.alert-success {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(46,125,50,0.08); color: #2E7D32;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}
.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}

/* Floating Save */
.save-floating {
  position: fixed; bottom: 24px; right: 24px; z-index: 100;
}
.save-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 14px 22px; background: #0a1628; color: white;
  border: none; border-radius: 12px; font-size: 14px; font-weight: 700;
  cursor: pointer; transition: all 0.15s;
  box-shadow: 0 8px 24px rgba(10,22,40,0.25);
  font-family: inherit;
}
.save-btn:hover:not(:disabled) { background: #1d3457; transform: translateY(-1px); }
.save-btn:disabled { opacity: 1; cursor: default; }
.save-btn.save-btn-success {
  background: #2E7D32 !important;
  box-shadow: 0 8px 24px rgba(46,125,50,0.30);
  animation: saveBtnPop 0.4s cubic-bezier(0.34,1.56,0.64,1);
}
@keyframes saveBtnPop {
  0%   { transform: scale(0.92); }
  60%  { transform: scale(1.04); }
  100% { transform: scale(1); }
}

.slide-fade-enter-active, .slide-fade-leave-active { transition: all 0.25s cubic-bezier(0.16,1,0.3,1); }
.slide-fade-enter-from, .slide-fade-leave-to { opacity: 0; transform: translateY(20px); }

/* === Toast === */
.toast-stack {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 2100;
  pointer-events: none;
}
.toast {
  display: flex; align-items: flex-start; gap: 11px;
  min-width: 300px; max-width: 380px;
  padding: 12px 36px 12px 14px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(10,22,40,0.04), 0 8px 24px rgba(10,22,40,0.10);
  cursor: pointer;
  pointer-events: auto;
  position: relative;
}
.toast-icon {
  width: 28px; height: 28px;
  flex-shrink: 0;
  border-radius: 7px;
  background: #2E7D32;
  display: flex; align-items: center; justify-content: center;
}
.toast-icon-error { background: #D32F2F; }
.toast-body { flex: 1; min-width: 0; }
.toast-title { font-size: 13px; font-weight: 700; color: #0a1628; }
.toast-message {
  font-size: 12px; color: #6b7280; margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.toast-close {
  position: absolute; top: 8px; right: 8px;
  width: 20px; height: 20px; padding: 0;
  background: transparent; border: none; border-radius: 4px;
  color: #c5cbd3; cursor: pointer;
  transition: all 0.15s;
}
.toast-close:hover { background: #f0f2f5; color: #6b7280; }

.toast-enter-active { transition: opacity 0.24s cubic-bezier(0.32,0.72,0,1), transform 0.32s cubic-bezier(0.32,0.72,0,1); }
.toast-leave-active { transition: opacity 0.18s ease-in, transform 0.18s ease-in; }
.toast-enter-from { opacity: 0; transform: translateX(20px); }
.toast-leave-to   { opacity: 0; transform: translateX(20px); }
.toast-move { transition: transform 0.32s cubic-bezier(0.32,0.72,0,1); }

/* Skeleton */
.skel-card {
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 16px;
}
.skel-big { height: 480px; }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

@media (max-width: 960px) {
  .grid-split { grid-template-columns: 1fr; }
  .avatar-side { position: static; }
  .form-row { grid-template-columns: 1fr; }
  .save-floating { left: 16px; right: 16px; }
  .save-btn { width: 100%; justify-content: center; }
}
</style>
